import { gql } from "lib/apolloClient";
import {
  fragments,
  calendarEventTagFragment,
} from "SchoolCalendar/modules/CalendarFragment";
import {
  academicYearFragment,
  studentsAlertFragment,
} from "modules/CommonFragments";
import {
  deadlineFragments,
  supervisorFragments,
} from "Projects/modules/ProjectFragments";

export const courseItemFragment = {
  courseItem: gql`
    fragment courseItem on Course {
      id
      title
      isDemo
      grade {
        id
        name
        displaySequence
      }
      calendar {
        id
      }
      curriculumProgram {
        id
        type
      }
      academicYears {
        ...academicYearItem
      }
      subjects {
        id
        type
      }
      primaryGrade {
        id
      }
      genericTags {
        id
        type
        label
      }
      enabledModules(filters: $enabledModulesFilters)
      aclControlParameters(filters: $aclControlParameterFilter) {
        id
        uid
        permission
        value
      }
      learningCourse {
        id
        title
        academicCourse {
          id
          label
        }
      }
    }
    ${academicYearFragment.academicYear}
  `,
};

export const eventItemFragment = {
  assignmentItem: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      state {
        state
        publishedAt
        updatedAt
      }
      content {
        id
        ... on Assessment {
          id
          title {
            id
            value
          }
          assessmentType {
            id
            value
          }
        }
        ... on AssignmentResource {
          id
          label
          assignmentTitle: title
          meeting {
            id
            label
            createdBy {
              id
              firstName
              lastName
              middleName
              profileImage
              preferredName
              prefix
              suffix
              pronouns
            }
            urls {
              type
              ... on OtherMeetingUrls {
                url
              }
              ... on ZoomMeetingUrls {
                joinUrl
                startUrl
              }
              ... on MicrosoftTeamMeetingUrls {
                joinUrl
              }
              ... on GoogleMeetingUrls {
                type
                url
              }
            }
            type
          }
        }
      }
      createdBy {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        suffix
        pronouns
      }
    }
  `,
  calendarEventItem: gql`
    fragment calendarEventItem on CalendarEventItem {
      id
      label
      description
      isFullDayEvent
      meeting {
        id
        label
        createdBy {
          id
          firstName
          middleName
          lastName
          profileImage
          preferredName
          prefix
          suffix
          pronouns
        }
        urls {
          type
          ... on OtherMeetingUrls {
            url
          }
          ... on ZoomMeetingUrls {
            joinUrl
            startUrl
          }
          ... on MicrosoftTeamMeetingUrls {
            joinUrl
          }
          ... on GoogleMeetingUrls {
            type
            url
          }
        }
        type
      }
      eventType
      tag: eventTag {
        id
        label
        colorCode
        uid
      }
      isFullDayEvent
      recurringSetting {
        frequency
        weekDays
        interval
        endDate
        monthDays
        setPositions
      }
    }
  `,
};

export const calendarEventFragment = {
  studentItem: gql`
    fragment studentItem on Student {
      id
      calendarEvents(filters: $filters) {
        id
        startTime
        endTime
        itemType
        item {
          id
          ... on Assignment {
            ...assignmentItem
          }

          ... on CalendarEventItem {
            ...calendarEventItem
          }

          ... on CalendarEvent {
            ...calendarEvent
          }
          ... on ProjectDeadline {
            ...projectDeadline
          }
        }
        calendars {
          id
          parentType
          parent {
            ... on Course {
              ...courseItem
            }
          }
        }
      }
    }
    ${eventItemFragment.assignmentItem}
    ${eventItemFragment.calendarEventItem}
    ${fragments.calendarEvent}
    ${courseItemFragment.courseItem}
    ${deadlineFragments.calendarProjectDeadlineFragment}
  `,
  staffItem: gql`
    fragment staffItem on Staff {
      id
      calendarEvents(filters: $filters) {
        id
        startTime
        endTime
        itemType
        item {
          id
          ... on Assignment {
            ...assignmentItem
          }

          ... on CalendarEventItem {
            ...calendarEventItem
          }

          ... on CalendarEvent {
            ...calendarEvent
          }
          ... on ProjectGroupDeadline {
            ...deadline
          }
        }

        calendars {
          id
          parentType
          parent {
            ... on Course {
              ...courseItem
            }
          }
        }
      }
    }

    ${eventItemFragment.assignmentItem}
    ${eventItemFragment.calendarEventItem}
    ${fragments.calendarEvent}
    ${courseItemFragment.courseItem}
    ${deadlineFragments.calendarDeadlineFragment}
  `,
};

const calendarProjectGroupFragment = gql`
  fragment calendarProjectGroupItem on ProjectGroup {
    id
    type
    name
    subType
    yearGroup {
      id
      name
    }
    subject {
      id
      name
    }
  }
`;

export const calendarEventDetailFragment = {
  studentItem: gql`
    fragment studentItem on Student {
      id
      profileImage
      firstName
      middleName
      lastName
      pronouns
      preferredName
      suffix
      prefix
      alertTypes {
        ...alertTypeFields
      }
      yearGroup {
        id
        grade {
          id
          name
        }
      }
    }
    ${studentsAlertFragment}
  `,
  staffItem: gql`
    fragment staffItem on Staff {
      id
      profileImage
      firstName
      lastName
      middleName
      prefix
      preferredName
      pronouns
      suffix
    }
  `,
  courseItem: gql`
    fragment courseItem on Course {
      id
      title
      learningCourse {
        id
        title
        academicCourse {
          id
          label
        }
      }
      allStudents {
        totalCount
        edges {
          node {
            id
            firstName
          }
        }
      }
    }
  `,
  meetingItem: gql`
    fragment meetingItem on Meeting {
      id
      label
      urls {
        ... on OtherMeetingUrls {
          type
          url
        }
        ... on ZoomMeetingUrls {
          type
          joinUrl
          startUrl
          meetingCode
        }
        ... on MicrosoftTeamMeetingUrls {
          type
          joinUrl
          joinWebUrl
          meetingCode
          passcode
        }
        ... on GoogleMeetingUrls {
          type
          url
          meetingCode
        }
      }
      type
      createdBy {
        id
      }
    }
  `,
};

const eventItemMetaFragment = {
  assignmentItem: gql`
    fragment assignmentItem on Assignment {
      id
      contentType
      state {
        state
        publishedAt
        updatedAt
      }
      createdBy {
        id
        email
        firstName
        lastName
        middleName
        profileImage
      }
      content {
        id
        ... on Assessment {
          id
          title {
            id
            value
          }
          assessmentType {
            id
            value
          }
        }
        ... on AssignmentResource {
          id
          label
          assignmentTitle: title
        }
      }
      stringDeadline: deadline
      publishedAt
      closeSubmissionDate
      visibility
      isPrivate
    }
  `,
  calendarEventItem: gql`
    fragment calendarEventItem on CalendarEventItem {
      id
      eventType
      label
      isFullDayEvent
      tag: eventTag {
        id
        label
        colorCode
        uid
      }
      location
      description
      createdBy {
        id
        email
        firstName
        lastName
        middleName
        profileImage
      }
      meeting {
        id
        label
        urls {
          ... on OtherMeetingUrls {
            type
            url
          }
          ... on ZoomMeetingUrls {
            type
            joinUrl
            startUrl
            meetingCode
          }
          ... on MicrosoftTeamMeetingUrls {
            type
            joinUrl
            joinWebUrl
            meetingCode
            passcode
          }
          ... on GoogleMeetingUrls {
            type
            url
            meetingCode
          }
        }
        type
        createdBy {
          id
          email
          firstName
          lastName
          middleName
          profileImage
        }
      }
      repeatEventSettings: recurringSetting {
        frequency
        weekDays
        interval
        endDate
        monthDays
        setPositions
        count
        timezone
      }
      metadata {
        skipNonOperationalDays
        recurrenceType
      }
      sharedWith
      attachments {
        id
        type
        name
        url
        metadata
        displaySequence
        mimeType
      }
    }
  `,
  calendarEventItemStaff: gql`
    fragment calendarEventItemStaff on CalendarEventItem {
      id
      eventType
      label
      isFullDayEvent
      tag: eventTag {
        id
        label
        colorCode
        uid
      }
    }
  `,
  calendarEvent: gql`
    fragment calendarEvent on CalendarEvent {
      id
      title
      start: startDate
      end: endDate
      tag {
        ...calendarEventTagItem
      }
    }
    ${calendarEventTagFragment.calendarEventTag}
  `,
  projectDeadline: gql`
    fragment projectDeadline on ProjectDeadline {
      id
      project {
        id
      }
      deadline {
        id
        deadlineTitle: title
        deadlineDate
        isDeadlineAllDay
        creatorUser: createdBy {
          ...supervisorInfo
        }
        projectGroup {
          ...calendarProjectGroupItem
        }
      }
      isDeadlineCompleted
    }
    ${supervisorFragments.supervisorInfoFragment}
    ${calendarProjectGroupFragment}
  `,
};

export const calendarEventMetaFragment = {
  studentItem: gql`
    fragment studentItem on Student {
      id
      calendarEvents(filters: $filters) {
        id
        startTime
        endTime
        itemType
        item {
          id
          ... on Assignment {
            ...assignmentItem
          }

          ... on CalendarEventItem {
            ...calendarEventItem
          }

          ... on CalendarEvent {
            ...calendarEvent
          }
          ... on ProjectDeadline {
            ...projectDeadline
          }

          ... on ProjectTaskResponse {
            id
            mappedProject: project {
              id
              projectGroup {
                ...calendarProjectGroupItem
              }
            }
            task {
              id
              dueOn
              deadlineTitle: title
              isDeadlineAllDay
              creatorUser: createdBy {
                ...supervisorInfo
              }
              projectGroup {
                ...calendarProjectGroupItem
              }
            }
          }
        }
      }
    }
    ${eventItemMetaFragment.assignmentItem}
    ${eventItemMetaFragment.calendarEventItem}
    ${eventItemMetaFragment.calendarEvent}
    ${eventItemMetaFragment.projectDeadline}
    ${supervisorFragments.supervisorInfoFragment}
    ${calendarProjectGroupFragment}
  `,
  staffItem: gql`
    fragment staffItem on Staff {
      id
      calendarEvents(filters: $filters) {
        id
        startTime
        endTime
        itemType
        item {
          id
          ... on Assignment {
            ...assignmentItem
          }

          ... on CalendarEventItem {
            ...calendarEventItemStaff
          }

          ... on CalendarEvent {
            ...calendarEvent
          }
          ... on ProjectGroupDeadline {
            id
            deadlineDate
            deadlineTitle: title
            isDeadlineAllDay
            description
            creatorUser: createdBy {
              ...supervisorInfo
            }
            projectGroup {
              ...calendarProjectGroupItem
            }
          }
          ... on ProjectGroupTask {
            id
            dueOn
            deadlineTitle: title
            isDeadlineAllDay
            description
            creatorUser: createdBy {
              ...supervisorInfo
            }
            projectGroup {
              ...calendarProjectGroupItem
            }
          }
        }
      }
    }
    ${eventItemMetaFragment.assignmentItem}
    ${eventItemMetaFragment.calendarEventItemStaff}
    ${eventItemMetaFragment.calendarEvent}
    ${supervisorFragments.supervisorInfoFragment}
    ${calendarProjectGroupFragment}
  `,
};

export const calendarEventAssignedDataFragment = {
  staffItem: gql`
    fragment staffItem on Staff {
      id
      calendarEvents(filters: $filters) {
        id
        startTime
        endTime
        itemType
        item {
          id
          ... on Assignment {
            course {
              id
              title
              learningCourse {
                id
                title
                displayName
              }
            }
          }
          ... on CalendarEventItem {
            id
            users {
              id
            }
            associatedEntities(filters: $associatedEntitiesFilters) {
              id
              ... on Grade {
                id
                name
              }
              ... on Course {
                id
                title
                learningCourse {
                  id
                  title
                  displayName
                }
              }
            }
          }
          ... on ProjectGroupDeadline {
            id
            projectGroup {
              yearGroup {
                id
                name
              }
            }
          }
          ... on ProjectGroupTask {
            id
            projectGroup {
              yearGroup {
                id
                name
              }
            }
          }
        }
      }
    }
  `,
};

export const courseCalendarDetailFragment = {
  studentItem: gql`
    fragment studentItem on Student {
      id
      courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
        ...courseItem
      }
    }
    ${courseItemFragment.courseItem}
  `,
  staffItem: gql`
    fragment staffItem on Staff {
      id
      coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
        edges {
          node {
            ...courseItem
          }
        }
      }
    }
    ${courseItemFragment.courseItem}
  `,
};

export const platformCalendarEventFragments = {
  platformCalendarEventDetails: gql`
    fragment platformCalendarEventItem on PlatformCalendarEvent {
      id
      startTime
      endTime
      calendars {
        id
        parentType
        parent {
          ... on Course {
            id
          }
          ... on Student {
            id
          }
        }
      }
      itemType
      item {
        ... on Assignment {
          id
        }
        ... on CalendarEventItem {
          id
        }
      }
    }
  `,
};

export const videoMeetingFragments = {
  videoMeetingDetails: gql`
    fragment videoMeetingItem on PlatformCalendarEvent {
      id
      startTime
      endTime
      itemType
      item {
        ... on CalendarEventItem {
          id
        }
      }
    }
  `,
};

export const basicUserFragment = {
  userDetailItem: gql`
    fragment userDetailItem on User {
      id
      email
      firstName
      lastName
      middleName
      type
      profileImage

      ... on Student {
        isArchived
        isDeleted
        preferredName
        pronouns
        prefix
        suffix

        yearGroup {
          id
          grade {
            id
            name
          }
        }

        familyMembers {
          id
          email
          firstName
          lastName
          middleName
          type
          profileImage
          prefix
          preferredName
          pronouns
          suffix

          children(organizationId: $organizationId) {
            id
            email
            firstName
            lastName
            middleName
            preferredName
            pronouns
            suffix
            prefix
          }
        }
      }

      ... on Staff {
        isArchived
        isDeleted
        displayRole
        role
        prefix
        preferredName
        pronouns
        suffix
      }

      ... on FamilyMember {
        children(organizationId: $organizationId) {
          id
          email
          firstName
          lastName
          middleName
          preferredName
          pronouns
          suffix
          prefix
        }
      }
      ... on Parent {
        children {
          id
          email
          firstName
          lastName
          middleName
          preferredName
          pronouns
          suffix
          prefix
        }
      }
    }
  `,
};
